import { ReactNode } from 'react';

import { FullProject, LoggedUser } from 'entities';

import { IconProps } from 'components/Icons';

import { ProjectModal, useProjectProfile } from '../context';
import { MultiKeyPressParams, useUser } from 'hooks';

import { GetLabelFn } from 'types';

type OptionHookResult = {
  getLabel?: GetLabelFn;

  loading?: boolean;

  isEnabled: boolean;

  action: () => void | Promise<void>;
};

export type UseProjectOption = () => OptionHookResult;

type CreatorParams = {
  loggedUser: LoggedUser;
  project: FullProject;
  openModal: (modal: ProjectModal) => void;
};

type UseHookCreator = (params: CreatorParams) => OptionHookResult;

function createProjectOptionHook(creator: UseHookCreator): UseProjectOption {
  return () => {
    const loggedUser = useUser();
    const {
      project,
      projectModalConfig: { openModal },
    } = useProjectProfile();

    return creator({
      loggedUser,
      openModal,
      project,
    });
  };
}

export type ProjectOptionOverlayProps = {
  isOpen: boolean;
  close: () => void;
};

export type ProjectAction =
  | 'ADD_SERVICE'
  | 'ADD_COUNTRY'
  | 'CHANGE_OWNER'
  | 'CHANGE_INVOICE_OWNER'
  | 'CHANGE_TEAM'
  | 'CHANGE_DATE'
  | 'DOWNLOAD_SIMPLE_PDF'
  | 'DOWNLOAD_SERVICE_BREAKDOWN_PDF'
  | 'REACTIVATE_PROJECT';

type ProjectOptionCommonParams = {
  getLabel?: GetLabelFn;

  Icon: ((props: IconProps) => JSX.Element) | ReactNode;

  useOption: UseHookCreator;

  shortcut?: Pick<MultiKeyPressParams, 'key' | 'triggerKey'> & {
    /**
     * If you need more control over the trigger,
     * you can use this to simply display the shortcut visually to the user
     */
    visualOnly?: boolean;
  };
};

type OptionTypeParams =
  | {
      modal: ProjectModal;

      overlay: (props: ProjectOptionOverlayProps) => JSX.Element | null;

      action?: never;

      /**
       * If this is true, the option won't show inside the list,
       * but will still be able to be called with shortcuts and/or direct action calls
       */
      hidden?: boolean;
    }
  | { modal?: never; overlay?: never; action: ProjectAction; hidden?: never };

export type ProjectOptionParams = ProjectOptionCommonParams & OptionTypeParams;

export function createProjectOption({ useOption, ...params }: ProjectOptionParams) {
  return {
    useProjectAction: createProjectOptionHook(useOption),
    ...params,
  };
}

export type ProjectOptionConfig = ReturnType<typeof createProjectOption>;

export type ProjectOption = ProjectAction | ProjectModal;
